// ============================================
// FILE: /frontend/src/utils/tierCheck.js
// PURPOSE: Frontend utility for tier checking and validation
// FIXED: Property names now match backend API response
// ============================================

import api from '../api/client';

/**
 * Tier configuration (matches backend)
 */
export const TIER_CONFIG = {
  BRONZE: {
    name: 'Bronze',
    price: 2650,
    adsLimit: 10,
    icon: '🥉',
  },
  SILVER: {
    name: 'Silver',
    price: 5350,
    adsLimit: 40,
    icon: '🥈',
  },
  GOLD: {
    name: 'Gold',
    price: 8450,
    adsLimit: 100,
    icon: '🥇',
  },
  PLATINUM: {
    name: 'Platinum',
    price: 9850,
    adsLimit: 140,
    icon: '💎',
  },
};

/**
 * Check if user can create a listing
 * ⭐ FIXED: Now fetches fresh user data from backend instead of relying on stale context
 * @returns {Promise<Object>} - { canCreate: boolean, reason: string, needsUpgrade: boolean, requiresPayment: boolean }
 */

export const checkUserCanCreateListing = async () => {
  console.log('[TIER_CHECK] Fetching fresh user data from backend...');

  try {

    // ⭐ Fetch fresh user data from backend
    const response = await api.get('/auth/me');
    const user = response.data.user;  // ⭐ Access nested user object

    console.log('[TIER_CHECK] Fresh user data received:', {
      tier: user?.subscriptionTier,
      used: user?.adsUsedThisMonth,
      limit: user?.adsLimit,
      endDate: user?.subscriptionEndDate,
    });

    if (!user) {
      return {
        canCreate: false,
        reason: 'User not authenticated',
        needsUpgrade: false,
        requiresPayment: false,
      };
    }

    // ✅ FIXED: Check if user has never subscribed (first-time user)
    if (!user.subscriptionTier || !user.subscriptionEndDate) {
      console.log('[TIER_CHECK] ❌ No subscription found - first-time user needs payment');
      return {
        canCreate: false,
        reason: 'Please select a subscription plan to create your first listing',
        needsUpgrade: true,
        requiresPayment: true, // TRUE = First-time user, send to checkout
        tier: null,
        currentUsage: 0,
        limit: 0,
      };
    }

    // ✅ FIXED: Use correct property name from database
    const tier = user.subscriptionTier;
    const tierConfig = TIER_CONFIG[tier];

    if (!tierConfig) {
      return {
        canCreate: false,
        reason: 'Invalid subscription tier',
        needsUpgrade: false,
        requiresPayment: false,
      };
    }

    // ✅ FIXED: Check if subscription is expired using subscriptionEndDate
    if (user.subscriptionEndDate && new Date(user.subscriptionEndDate) < new Date()) {
      console.log('[TIER_CHECK] ⚠️ Subscription expired - needs new subscription');
      return {
        canCreate: false,
        reason: 'Your subscription has expired. Please choose a plan to continue.',
        needsUpgrade: false,
        requiresPayment: true, // TRUE = Send to checkout to choose ANY plan
        isExpired: true,
        previousTier: tier,
        currentUsage: user.adsUsedThisMonth || 0,
        limit: user.adsLimit || tierConfig.adsLimit,
      };
    }

    // ✅ FIXED: Use adsUsedThisMonth from database
    const currentUsage = user.adsUsedThisMonth || 0;
    const limit = user.adsLimit || tierConfig.adsLimit;

    if (currentUsage >= limit) {
      console.log('[TIER_CHECK] ⚠️ Ad limit reached - needs upgrade');
      return {
        canCreate: false,
        reason: `You've reached your ${tier} tier limit of ${limit} ads.`,
        needsUpgrade: true,
        requiresPayment: false, // FALSE = Has active subscription, just hit limit
        tier,
        currentUsage,
        limit,
      };
    }

    console.log('[TIER_CHECK] ✅ User can create listing');
    return {
      canCreate: true,
      reason: 'OK',
      requiresPayment: false,
      tier,
      currentUsage,
      limit,
    };
  } catch (error) {
    console.error('[TIER_CHECK] Error fetching user data:', error);
    return {
      canCreate: false,
      reason: 'Failed to verify subscription status. Please try again.',
      needsUpgrade: false,
      requiresPayment: false,
      error: true,
    };
  }
};


/**
 * Get tier information by tier name
 * @param {String} tierName - Tier name (e.g., 'GOLD')
 * @returns {Object} - Tier configuration
 */
export const getTierInfo = (tierName) => {
  return TIER_CONFIG[tierName] || TIER_CONFIG.BRONZE;
};

/**
 * Get available upgrade tiers for a user
 * @param {String} currentTier - Current tier name
 * @returns {Array} - Array of tier objects user can upgrade to
 */
export const getAvailableUpgrades = (currentTier) => {
  const tierOrder = ['BRONZE', 'SILVER', 'GOLD', 'PLATINUM'];
  const currentIndex = tierOrder.indexOf(currentTier || 'BRONZE');
  
  return tierOrder.slice(currentIndex + 1).map((tierName) => ({
    tier: tierName,
    ...TIER_CONFIG[tierName],
  }));
};

/**
 * Calculate prorated upgrade cost (frontend preview)
 * @param {Object} user - User object
 * @param {String} newTier - New tier to upgrade to
 * @returns {Object} - Prorated calculation
 */
export const calculateProratedUpgrade = (user, newTier) => {
  // ✅ FIXED: Use correct property name from database
  const currentTier = user.subscriptionTier || 'BRONZE';
  const currentConfig = TIER_CONFIG[currentTier];
  const newConfig = TIER_CONFIG[newTier];

  if (!currentConfig || !newConfig) {
    return {
      proratedAmount: newConfig.price,
      fullPrice: newConfig.price,
      savings: 0,
    };
  }

  // ✅ FIXED: Use subscriptionEndDate from database
  if (!user.subscriptionEndDate || new Date(user.subscriptionEndDate) < new Date()) {
    return {
      proratedAmount: newConfig.price,
      fullPrice: newConfig.price,
      savings: 0,
      daysRemaining: 0,
    };
  }

  // Calculate days remaining
  const now = new Date();
  const endDate = new Date(user.subscriptionEndDate);
  const daysRemaining = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));

  if (daysRemaining <= 0) {
    return {
      proratedAmount: newConfig.price,
      fullPrice: newConfig.price,
      savings: 0,
      daysRemaining: 0,
    };
  }

  // Calculate unused credit
  const dailyRate = currentConfig.price / 30;
  const unusedCredit = Math.floor(dailyRate * daysRemaining);
  const proratedAmount = Math.max(0, newConfig.price - unusedCredit);

  return {
    proratedAmount,
    fullPrice: newConfig.price,
    savings: unusedCredit,
    daysRemaining,
    unusedCredit,
  };
};

/**
 * Format tier limit for display
 * @param {Number} limit
 * @returns {String}
 */
export const formatTierLimit = (limit) => {
  if (limit >= 999999) {
    return 'Unlimited';
  }
  return limit.toString();
};

/**
 * Get tier color for UI
 * @param {String} tierName
 * @returns {String} - Tailwind color class
 */
export const getTierColor = (tierName) => {
  const colors = {
    BRONZE: 'text-orange-700',
    SILVER: 'text-gray-600',
    GOLD: 'text-yellow-600',
    PLATINUM: 'text-purple-600',
  };
  return colors[tierName] || colors.BRONZE;
};

/**
 * Get tier badge color for UI
 * @param {String} tierName
 * @returns {String} - Tailwind badge classes
 */
export const getTierBadgeColor = (tierName) => {
  const colors = {
    BRONZE: 'bg-orange-100 text-orange-800 border-orange-300',
    SILVER: 'bg-gray-100 text-gray-800 border-gray-300',
    GOLD: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    PLATINUM: 'bg-purple-100 text-purple-800 border-purple-300',
  };
  return colors[tierName] || colors.BRONZE;
};

/**
 * Validate if upgrade is valid
 * @param {String} currentTier
 * @param {String} newTier
 * @returns {Boolean}
 */
export const isValidUpgrade = (currentTier, newTier) => {
  const tierOrder = ['BRONZE', 'SILVER', 'GOLD', 'PLATINUM'];
  const currentIndex = tierOrder.indexOf(currentTier || 'BRONZE');
  const newIndex = tierOrder.indexOf(newTier);
  
  return newIndex > currentIndex;
};

/**
 * API call to check tier status from backend
 * @returns {Promise<Object>}
 */
export const fetchTierStatus = async () => {
  try {
    const response = await api.get('/subscription/current');
    return response.data;
  } catch (error) {
    console.error('[TIER_CHECK] Failed to fetch tier status:', error);
    throw error;
  }
};